import { useState } from "react";
import { Download, Expand, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DocumentTypeEnum,
  parseDocumentType,
  DOCUMENT_TYPE_ENUM_LABELS,
} from "../utils/documentTypeFromExtension";
import { formatFileSize } from "../utils/formatFileSize";
import { Modal, VisibilityToggleIcon } from "../../../../../components/shared";
import type { DocumentRecord } from "./EditDocumentModal";

interface DocumentCardProps {
  document: DocumentRecord;
  canManage: boolean;
  onEdit: () => void;
  onDeleteClick: () => void;
  onVisibilityToggle?: (doc: DocumentRecord) => void;
  visibilityLoading?: boolean;
  deleteLoading?: boolean;
}

function typeLabel(doc: DocumentRecord): string {
  const type = parseDocumentType(doc.documentType);
  return DOCUMENT_TYPE_ENUM_LABELS[type];
}

export default function DocumentCard({
  document: doc,
  canManage,
  onEdit,
  onDeleteClick,
  onVisibilityToggle,
  visibilityLoading = false,
  deleteLoading = false,
}: DocumentCardProps) {
  const visibility = (doc.visibility?.toLowerCase() === "shared" ? "shared" : "internal") as "internal" | "shared";
  const displayTitle = doc.title ?? doc.filename ?? "Sem título";
  const documentType = parseDocumentType(doc.documentType);
  const isImage = documentType === DocumentTypeEnum.Image;
  const [expandOpen, setExpandOpen] = useState(false);

  return (
    <>
      <Card
        role={canManage ? "button" : undefined}
        tabIndex={canManage ? 0 : undefined}
        onClick={() => canManage && onEdit()}
        onKeyDown={(e) => {
          if (canManage && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onEdit();
          }
        }}
        className={`transition-colors duration-300 text-left ${canManage ? "cursor-pointer hover:border-primary-400 dark:hover:border-primary-600" : "cursor-default"}`}
      >
        <CardContent className="flex items-center gap-4 py-3 px-4">
          {/* Thumbnail */}
          <div className="relative w-14 h-14 shrink-0 overflow-hidden rounded-none bg-primary-100 dark:bg-primary-800 border border-primary-200 dark:border-primary-700 flex items-center justify-center">
            {isImage && doc.url ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandOpen(true);
                }}
                className="w-full h-full relative"
                aria-label="Expandir imagem"
              >
                <img
                  src={doc.url}
                  alt=""
                  className="w-full h-full object-contain"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-primary-950/30 dark:bg-primary-950/40 pointer-events-none">
                  <Expand className="w-6 h-6 text-white/90" strokeWidth={1.5} />
                </span>
              </Button>
            ) : (
              <FileText className="w-6 h-6 text-primary-500 dark:text-primary-400" strokeWidth={1.5} />
            )}
          </div>

          {/* Title + Tipo + Tamanho */}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="font-light tracking-tight text-primary-950 dark:text-white truncate block">
              {displayTitle}
            </span>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-0 text-xs text-primary-600 dark:text-primary-400">
              <span>Tipo: {typeLabel(doc)}</span>
              {typeof doc.size === "number" && (
                <span>Tamanho: {formatFileSize(doc.size)}</span>
              )}
            </div>
          </div>

          {/* Actions: Visibility, Download, Excluir */}
          <div className="flex items-center gap-1 shrink-0">
            {canManage && onVisibilityToggle && (
              <span onClick={(e) => e.stopPropagation()}>
                <VisibilityToggleIcon
                  visibility={visibility}
                  onClick={() => onVisibilityToggle(doc)}
                  disabled={visibilityLoading}
                  iconSize="sm"
                />
              </span>
            )}
            {doc.url && (
              <Button asChild variant="ghost" size="icon" className="p-2">
                <a
                  href={doc.url}
                  download
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Baixar"
                >
                  <Download className="w-5 h-5" strokeWidth={1.5} />
                </a>
              </Button>
            )}
            {canManage && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick();
                }}
                disabled={deleteLoading}
                className="p-2 text-red-600 hover:text-red-800 hover:bg-transparent dark:hover:bg-transparent"
                aria-label="Excluir"
              >
                <Trash2 className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {isImage && doc.url && (
        <Modal
          isOpen={expandOpen}
          onClose={() => setExpandOpen(false)}
          closeOnOverlayClick
          maxWidth="max-w-3xl"
        >
          <div className="flex items-center justify-center p-4 min-h-[50vh]">
            <img
              src={doc.url}
              alt={displayTitle}
              className="max-w-full max-h-[80vh] w-auto h-auto object-contain"
            />
          </div>
        </Modal>
      )}
    </>
  );
}
